import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { SessionProvider, useSession } from "./lib/session";
import { SignIn } from "./screens/SignIn";
import { Overview } from "./screens/Overview";
import { Movements } from "./screens/Movements";
import { Wallets } from "./screens/Wallets";
import { Reconciliation } from "./screens/Reconciliation";
import { Queues } from "./screens/Queues";
import { Fees } from "./screens/Fees";
import { Audit } from "./screens/Audit";
import { Flags } from "./screens/Flags";
import { Team } from "./screens/Team";
import { Setup } from "./screens/Setup";

const NAV = [
  ["/", "Overview"],
  ["/movements", "Movements"],
  ["/wallets", "Wallets"],
  ["/reconciliation", "Reconciliation"],
  ["/queues", "Queues"],
  ["/fees", "Fees"],
  ["/audit", "Audit"],
  ["/flags", "Flags"],
] as const;

// Only a super admin can grant access, so only they are shown the door to it.
const SUPER_NAV = [["/team", "Team"]] as const;

const Mark = ({ size = 17 }: { size?: number }) => (
  <svg viewBox="0 0 120 120" width={size} height={size} fill="none">
    <rect x="22" y="12" width="20" height="96" rx="10" fill="#F5F3EE" />
    <path fillRule="evenodd" fill="#F5F3EE" d="M28,76 a32,32 0 1,0 64,0 a32,32 0 1,0 -64,0 Z M46,76 a14,14 0 1,0 28,0 a14,14 0 1,0 -28,0 Z" />
  </svg>
);

function useTheme() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("bp.console.theme") === "dark"; } catch { return false; }
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    try { localStorage.setItem("bp.console.theme", dark ? "dark" : "light"); } catch { /* ignore */ }
  }, [dark]);
  return { dark, toggle: () => setDark((d) => !d) };
}

function Shell() {
  const { user, loading, expired, signOut, needsSetup, setupDone } = useSession();
  const theme = useTheme();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--muted)", animation: "bp-shim 1.4s infinite" }}>Checking your session…</div>
      </div>
    );
  }
  // Before ANY account exists, the console offers setup instead of a sign-in nobody could pass. Once one
  // exists this branch is never taken again — the page is gone, with nothing to switch off by hand.
  if (!user && needsSetup) return <Setup onDone={setupDone} />;
  if (!user) return <SignIn expired={expired} />;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", paddingBottom: 70 }}>
      <header style={{ position: "sticky", top: 0, zIndex: 40, background: "var(--header)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--line)", padding: "12px 22px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", rowGap: 9 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 6 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: "#1C2030", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Mark /></div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: "-.02em" }}>Payment Manager</div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--muted)", letterSpacing: ".05em" }}>STAFF ONLY</div>
          </div>
        </div>

        <nav style={{ display: "flex", gap: 5, flexWrap: "wrap", flex: 1, minWidth: 280 }}>
          {[...NAV, ...(user.role === "super_admin" ? SUPER_NAV : [])].map(([to, label]) => (
            <NavLink key={to} to={to} end={to === "/"}
              style={({ isActive }) => ({
                padding: "8px 12px", border: "none", borderRadius: 9, fontSize: 12, cursor: "pointer",
                background: isActive ? "var(--ink)" : "none",
                color: isActive ? "var(--bg)" : "var(--ink2)",
                fontWeight: isActive ? 800 : 700,
              })}>{label}</NavLink>
          ))}
        </nav>

        <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", marginRight: 2 }}>
            <span style={{ fontSize: 12, fontWeight: 800 }}>{user.name}</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: "var(--muted)", letterSpacing: ".04em" }}>
              {user.role === "super_admin" ? "SUPER ADMIN" : user.role.toUpperCase()}
            </span>
          </div>
          <button type="button" onClick={theme.toggle} style={btn}>{theme.dark ? "Light" : "Dark"}</button>
          <button type="button" onClick={() => void signOut()} style={btn}>Sign out</button>
        </div>
      </header>

      <main key={location.pathname} style={{ width: "100%", maxWidth: 1240, margin: "0 auto", padding: "22px 22px 0", display: "flex", flexDirection: "column", gap: 16 }}>
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/movements" element={<Movements />} />
          <Route path="/wallets" element={<Wallets />} />
          <Route path="/wallets/:tenantId" element={<Wallets />} />
          <Route path="/reconciliation" element={<Reconciliation />} />
          <Route path="/queues" element={<Queues />} />
          <Route path="/fees" element={<Fees />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/flags" element={<Flags />} />
          {/* Guarded on the server too — this only hides the door, it does not hold it shut. */}
          <Route path="/team" element={user.role === "super_admin" ? <Team /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

const btn: React.CSSProperties = {
  padding: "8px 12px", border: "1px solid var(--line2)", borderRadius: 10,
  background: "var(--surface)", color: "var(--ink2)", fontSize: 11.5, fontWeight: 800, cursor: "pointer",
};

export default function App() {
  return <SessionProvider><Shell /></SessionProvider>;
}
